import React, { useEffect, useReducer, useRef, useState } from 'react';
import { Divider, Stack, Tab, Tabs } from '@mui/material';
import { styles } from './styles';
import QuestionLibrary from './Question/QuestionLibrary';
import { InitialTemplateTableState, TemplateTableReducer } from './reducer';
import { questionsTableColumn, tagsTableColumn } from './config';
import StyledSnackbar, { ISnackbarInput } from '../Components/CustomUIElements/StyledSnackbar';
import TagManager from './TagManager/TagManager';
import { useFeatureFlag } from '../../FeatureFlags';
import { classes } from '../Job/styles';
import { formatUserPermissions } from '../Components/Utilities/formatUserPermissions';

export default function Templates() {
  const tagManagementEnabled = useFeatureFlag('tagManagementEnabled');
  const questionLibraryEnabled = useFeatureFlag('questionLibraryEnabled');
  const userPermissions = formatUserPermissions();

  // Determine the initial tab value based on enabled features
  const hasQuestionLibraryAccess =
    questionLibraryEnabled &&
    userPermissions?.['Entities']?.['Create / Edit / Delete Question Library'];
  const initialTabValue = hasQuestionLibraryAccess ? 0 : tagManagementEnabled ? 0 : -1;

  const [tabValue, setTabValue] = useState<number>(initialTabValue);
  const headerRefs = useRef<HTMLDivElement[]>([]);
  const [templatePageState, templatePageDispatch] = useReducer(
    TemplateTableReducer,
    InitialTemplateTableState
  );

  // Update tab selection when feature flags or permissions change
  useEffect(() => {
    const newTabValue = hasQuestionLibraryAccess ? 0 : tagManagementEnabled ? 0 : -1;
    setTabValue(newTabValue);

    // Update table columns based on the new tab value
    if (hasQuestionLibraryAccess && newTabValue === 0) {
      templatePageDispatch({ type: 'CHANGE_TABLE_TAB', payload: questionsTableColumn });
    } else if (tagManagementEnabled && newTabValue === 0) {
      templatePageDispatch({ type: 'CHANGE_TABLE_TAB', payload: tagsTableColumn });
    }
  }, [hasQuestionLibraryAccess, tagManagementEnabled]);

  return (
    <Stack sx={styles.templatesPageContainer}>
      <Stack sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
        <Stack sx={styles.templatesPageHeader}>Templates</Stack>
      </Stack>
      <Stack>
        <Tabs
          value={tabValue}
          onChange={(e, value) => {
            setTabValue(value);
            // Determine which table columns to use based on the selected tab and available features
            const isQuestionLibraryTab = hasQuestionLibraryAccess && value === 0;
            const isTagManagerTab =
              tagManagementEnabled && (hasQuestionLibraryAccess ? value === 1 : value === 0);

            if (isQuestionLibraryTab) {
              templatePageDispatch({ type: 'CHANGE_TABLE_TAB', payload: questionsTableColumn });
            } else if (isTagManagerTab) {
              templatePageDispatch({ type: 'CHANGE_TABLE_TAB', payload: tagsTableColumn });
            }
          }}
          variant="scrollable"
          scrollButtons={false}
          sx={{ ...classes.tabs, '& .MuiTabs-indicator': { background: '#5BC4C0' } }}
        >
          {hasQuestionLibraryAccess && (
            <Tab
              id="question-library-tab"
              label="Question library"
              ref={(el) => el && (headerRefs.current[0] = el)}
            />
          )}
          {tagManagementEnabled && userPermissions?.['App Menu']?.['Manage Tags'] && (
            <Tab
              id="tag-manager-tab"
              label="Tag Manager"
              ref={(el) => el && (headerRefs.current[hasQuestionLibraryAccess ? 1 : 0] = el)}
            />
          )}
        </Tabs>
        <Divider />
        {hasQuestionLibraryAccess && tabValue === 0 && (
          <QuestionLibrary
            QuestionState={templatePageState}
            QuestionDispatch={templatePageDispatch}
          />
        )}
        {tagManagementEnabled && userPermissions?.['App Menu']?.['Manage Tags'] &&
          ((hasQuestionLibraryAccess && tabValue === 1) ||
            (!hasQuestionLibraryAccess && tabValue === 0)) && (
            <TagManager {...{ templatePageState, templatePageDispatch }} />
          )}
      </Stack>

      {!!templatePageState.snackbar.message && (
        <StyledSnackbar
          message={templatePageState.snackbar.message}
          state={templatePageState.snackbar.state}
          setSnackbarState={(snackbarInput: ISnackbarInput) =>
            templatePageDispatch({ type: 'SET_SNACKBAR', payload: snackbarInput })
          }
        />
      )}
    </Stack>
  );
}
